import { create } from 'zustand'
import { useSnippetSelection } from '@/features/source-snippets/selection'
import type { GeoSourceDocument } from '@/services/api/geolocation'
import {
  type SchemaPreview,
  type SchemaPreviewMapRenderMode,
  schemasApi,
} from '@/services/api/schemas'

export interface GeoEntity {
  id: string
  /** Original-case entity label (e.g. "Vehicle"), used to resolve the
   *  schema-preview cache. Optional so legacy / mock data paths that do not
   *  consult the conventions module (sample data, standalone geolocation
   *  page) can omit it; in that case dispatch falls back to the data-shape
   *  defaults. */
  label?: string
  name: string
  type: string
  latitude: number
  longitude: number
  isStatic?: boolean
  /** Schema-driven render dispatch. `null` means the entity is not rendered on
   *  the map (e.g. PhoneNumber). When undefined, dispatch falls back to the
   *  data-shape defaults (animated_track when there is multi-point track
   *  data, static_pin otherwise). Replaces the legacy `geoDisplayMode`. */
  mapRenderMode?: SchemaPreviewMapRenderMode
  /** True for a bare Location anchor with no in-set entity attached — rendered
   *  dimmed to read as "a valid but unexplored place". */
  dimmed?: boolean
  properties?: Record<string, unknown>
}

export interface TrackPoint {
  latitude: number
  longitude: number
  altitude?: number // meters above mean sea level (MSL); undefined = ground level
  timestamp: string
}

export type BaseLayer = 'satellite' | 'streets' | 'hybrid' | 'dark' | 'dark-hc' | 'osm'
export type DataSource = 'files' | 'sample'

/**
 * Which slice of entities the bottom temporal strip is currently scoped to:
 *
 *  - `filter`   — axis spans ALL temporal entities (animated + pinned).
 *                 Handles hide entities outside the window. No playhead.
 *  - `playback` — axis spans only animated entities (so the playhead never
 *                 sits in dead zones outside the actual tracks). Transport
 *                 controls + playhead appear; wheel-zoom is allowed.
 *
 * Only meaningful when the case has both animated tracks AND time-anchored
 * pins; otherwise the strip is pinned to whichever mode has data.
 */
export type MapTemporalMode = 'filter' | 'playback'

/**
 * How the on-map tracks layer reacts to the timeline filter / scrubber.
 *
 * - `full` (default): tracks render their entire historical extent regardless
 *   of timeline filter or scrubber position. Best for the "bird's-eye"
 *   investigation view where the user wants to see full trajectories.
 * - `follow-scrubber`: tracks honour `filterStart`/`filterEnd` and dim past
 *   the playhead — same as the legacy behavior.
 * - `custom-range`: tracks honour an independent range stored in
 *   `tracksCustomRange` (set via the tracks-layer popover).
 */
export type TracksTemporalScope = 'full' | 'follow-scrubber' | 'custom-range'

interface GeolocationState {
  entities: GeoEntity[]
  selectedEntityId: string | null
  followedEntityId: string | null
  tracks: Record<string, TrackPoint[]>
  showTracks: boolean
  /**
   * Temporal scope for the tracks layer. Decoupled from the global scrubber
   * so the user can keep full trajectories visible while filtering events
   * elsewhere.
   */
  tracksTemporalScope: TracksTemporalScope
  /** Custom range used when `tracksTemporalScope === 'custom-range'`. */
  tracksCustomRange: { start: string; end: string } | null
  hiddenTrackEntityIds: Set<string>
  /** Per-entity hide for the MapLibre map view. Hides BOTH the pin and any
   *  associated track polyline — used by the entity rows in the map sidebar
   *  (eye toggle). Distinct from `hiddenTrackEntityIds`, which only hides
   *  the polyline (legacy Cesium-era control). */
  hiddenEntityIds: Set<string>
  /** Entity-type labels (e.g. "BTSStation", "MobileDevice") whose markers
   *  the user has hidden via the Layers popover. Empty by default — every
   *  type renders. Compared against `GeoEntity.label` (preferred) and
   *  `GeoEntity.type` as a fallback. */
  hiddenEntityTypes: Set<string>
  /** Per-entity overrides for the on-map track polyline colour. Keyed by
   *  GeoEntity.id; falls back to the type-driven default when missing. */
  trackColors: Record<string, string>
  baseLayer: BaseLayer
  dataSource: DataSource
  availableSources: GeoSourceDocument[]
  selectedSourceIds: Set<string>
  /** User-controlled visibility of the bottom temporal strip (Map view).
   *  Defaults to `true`; toggled from the View-settings sidebar tab. The
   *  strip is also hidden automatically when no time-aware entities exist
   *  (geolocation-view's own data check). */
  timelineVisible: boolean
  /** Which slice the bottom strip is scoped to — see {@link MapTemporalMode}.
   *  Defaults to `'filter'` so users get the universal filter on first
   *  load; only flips to `'playback'` when the user explicitly asks. */
  mapTemporalMode: MapTemporalMode
  preSelectedDocumentId: string | null
  /** Session-scoped cache for schema convention previews, keyed
   *  `${schemaId}:${label}`. Populated by `loadSchemaPreview` and read
   *  by `useInvestigationGeoData` when resolving each entity's
   *  `mapRenderMode`. */
  schemaPreviews: Map<string, SchemaPreview>

  setEntities: (entities: GeoEntity[]) => void
  selectEntity: (id: string | null) => void
  followEntity: (id: string | null) => void
  setTrack: (entityId: string, points: TrackPoint[]) => void
  setTracks: (trackMap: Record<string, TrackPoint[]>) => void
  setShowTracks: (show: boolean) => void
  setTracksTemporalScope: (scope: TracksTemporalScope) => void
  setTracksCustomRange: (range: { start: string; end: string } | null) => void
  toggleEntityTrack: (entityId: string) => void
  toggleEntityVisibility: (entityId: string) => void
  setTimelineVisible: (visible: boolean) => void
  setMapTemporalMode: (mode: MapTemporalMode) => void
  toggleEntityTypeVisibility: (typeLabel: string) => void
  setHiddenEntityTypes: (types: Set<string>) => void
  setEntityTrackColor: (entityId: string, color: string | null) => void
  setBaseLayer: (layer: BaseLayer) => void
  setDataSource: (source: DataSource) => void
  setAvailableSources: (sources: GeoSourceDocument[]) => void
  toggleSource: (id: string) => void
  selectAllSources: () => void
  deselectAllSources: () => void
  setSelectedSourceIds: (ids: Set<string>) => void
  setPreSelectedDocumentId: (id: string | null) => void
  setSchemaPreview: (schemaId: string, label: string, preview: SchemaPreview) => void
}

// Pick the default basemap to match the active app theme so the Geo view
// opens in the same colour register as the rest of the UI. Read directly
// from the same localStorage key + matchMedia the ThemeProvider uses;
// this runs at module init, before React mounts, so the store seeds with
// the right default and no flash of the wrong basemap occurs.
function detectInitialBaseLayer(): BaseLayer {
  if (typeof window === 'undefined') return 'dark-hc'
  try {
    const stored = window.localStorage.getItem('vite-ui-theme') as
      | 'dark'
      | 'light'
      | 'system'
      | null
    const theme = stored ?? 'dark'
    const isDark =
      theme === 'dark' ||
      (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches)
    return isDark ? 'dark-hc' : 'osm'
  } catch {
    return 'dark-hc'
  }
}

export const useGeolocationStore = create<GeolocationState>((set, _get) => ({
  entities: [],
  selectedEntityId: null,
  followedEntityId: null,
  tracks: {},
  showTracks: true,
  tracksTemporalScope: 'full',
  tracksCustomRange: null,
  hiddenTrackEntityIds: new Set<string>(),
  hiddenEntityIds: new Set<string>(),
  hiddenEntityTypes: new Set<string>(),
  trackColors: {},
  baseLayer: detectInitialBaseLayer(),
  dataSource: 'files',
  availableSources: [],
  selectedSourceIds: new Set<string>(),
  timelineVisible: true,
  mapTemporalMode: 'filter',
  preSelectedDocumentId: null,
  schemaPreviews: new Map<string, SchemaPreview>(),

  setEntities: entities => set({ entities }),

  selectEntity: id => {
    if (id) useSnippetSelection.getState().select(null, id, 'map')
    set({ selectedEntityId: id })
  },

  followEntity: id =>
    set(state => ({
      followedEntityId: state.followedEntityId === id ? null : id,
      selectedEntityId: id ?? state.selectedEntityId,
    })),

  setTrack: (entityId, points) =>
    set(state => ({
      tracks: { ...state.tracks, [entityId]: points },
    })),

  setTracks: trackMap =>
    set(state => ({
      tracks: { ...state.tracks, ...trackMap },
    })),

  setShowTracks: show => set({ showTracks: show }),
  setTracksTemporalScope: scope => set({ tracksTemporalScope: scope }),
  setTracksCustomRange: range => set({ tracksCustomRange: range }),
  toggleEntityTrack: entityId =>
    set(state => {
      const next = new Set(state.hiddenTrackEntityIds)
      if (next.has(entityId)) {
        next.delete(entityId)
      } else {
        next.add(entityId)
      }
      return { hiddenTrackEntityIds: next }
    }),
  toggleEntityVisibility: entityId =>
    set(state => {
      const next = new Set(state.hiddenEntityIds)
      if (next.has(entityId)) {
        next.delete(entityId)
      } else {
        next.add(entityId)
      }
      return { hiddenEntityIds: next }
    }),
  setTimelineVisible: visible => set({ timelineVisible: visible }),
  setMapTemporalMode: mode => set({ mapTemporalMode: mode }),
  toggleEntityTypeVisibility: typeLabel =>
    set(state => {
      const next = new Set(state.hiddenEntityTypes)
      if (next.has(typeLabel)) {
        next.delete(typeLabel)
      } else {
        next.add(typeLabel)
      }
      return { hiddenEntityTypes: next }
    }),
  setHiddenEntityTypes: types => set({ hiddenEntityTypes: types }),
  setEntityTrackColor: (entityId, color) =>
    set(state => {
      const next = { ...state.trackColors }
      if (color == null) delete next[entityId]
      else next[entityId] = color
      return { trackColors: next }
    }),
  setBaseLayer: layer => set({ baseLayer: layer }),
  setDataSource: source =>
    set({
      dataSource: source,
      entities: [],
      tracks: {},
      selectedEntityId: null,
      followedEntityId: null,
      hiddenTrackEntityIds: new Set<string>(),
      hiddenEntityIds: new Set<string>(),
      hiddenEntityTypes: new Set<string>(),
      trackColors: {},
      selectedSourceIds: new Set<string>(),
    }),
  setAvailableSources: sources => set({ availableSources: sources }),
  toggleSource: id =>
    set(state => {
      const next = new Set(state.selectedSourceIds)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return { selectedSourceIds: next }
    }),
  selectAllSources: () =>
    set(state => ({
      selectedSourceIds: new Set(state.availableSources.map(s => s.id)),
    })),
  deselectAllSources: () => set({ selectedSourceIds: new Set<string>() }),
  setSelectedSourceIds: ids => set({ selectedSourceIds: ids }),
  setPreSelectedDocumentId: id => set({ preSelectedDocumentId: id }),
  setSchemaPreview: (schemaId, label, preview) =>
    set(state => {
      const next = new Map(state.schemaPreviews)
      next.set(`${schemaId}:${label}`, preview)
      return { schemaPreviews: next }
    }),
}))

const inflightPreviews = new Map<string, Promise<SchemaPreview | null>>()

/**
 * Fetch the schema convention preview for a label and cache it in the
 * geolocation store. Concurrent calls for the same key share a single
 * inflight promise; subsequent calls return immediately from cache.
 *
 * Returns `null` when the lookup fails (e.g. label not in schema, 404).
 */
export async function loadSchemaPreview(
  schemaId: string,
  label: string,
  source: 'entity' | 'relation' = 'entity'
): Promise<SchemaPreview | null> {
  const key = `${schemaId}:${label}`
  const cached = useGeolocationStore.getState().schemaPreviews.get(key)
  if (cached) return cached

  const inflight = inflightPreviews.get(key)
  if (inflight) return inflight

  const promise = schemasApi
    .getTypePreview(schemaId, label, source)
    .then(preview => {
      useGeolocationStore.getState().setSchemaPreview(schemaId, label, preview)
      return preview
    })
    .catch(err => {
      console.warn(`Failed to load schema preview for ${key}:`, err)
      return null
    })
    .finally(() => {
      inflightPreviews.delete(key)
    })

  inflightPreviews.set(key, promise)
  return promise
}
